// Spin the Bottle in VR — fully playable with bare hands (poke or pinch the
// big buttons): the join panel over the circle, then a lap panel at your
// cushion with the lobby options, Spin / Truth / Dare / Done / Pass / Clap /
// Skip, the card, scores, vote to end, rules, leaderboard and (world owner)
// removing a player. Spinners can also pinch the bottle and flick it
// (BottleTable3D); the Spin button is always there. Table-local, floor origin.

import React, { useState } from 'react';
import { Text } from '@react-three/drei';
import { tableAction } from '../gameStore';
import { leaveTable } from '../kit/LobbyKit';
import { VRBtn, VRJoinPanel, VRLobbyBody, VRRulesPanel, VRLeaderboardPanel, SeatPanelMount, vrColors as colors, noRaycast, Surface } from '../kit/vrKit';
import { ROUND_CHOICES, type BottleOptions } from '../../../../supabase/functions/games/bottle.ts';
import type { VRPanelProps } from '../types';
import { type BottleState, statusLine, lastLine, scoreRows, winnerLine, sendMove, playerCount, majorityOf, useIsWorldOwner, OPTION_LABELS } from './bottleUi';

const W = 0.8;

function PlayersPanel({ pid, st, g, seat, owner, busy, onClose }: {
  pid: string; st: VRPanelProps['table']['state']; g: BottleState; seat: number; owner: boolean; busy: boolean; onClose: () => void;
}) {
  const rows = scoreRows(st, g);
  const H = 0.12 + rows.length * 0.055;
  return (
    <group position={[0, 0, 0.02]} onClick={(e) => e.stopPropagation()} onPointerOver={(e) => e.stopPropagation()}>
      <Surface width={W} height={H} color={colors.panel} />
      <Text raycast={noRaycast} position={[-W / 2 + 0.025, H / 2 - 0.035, 0.004]} anchorX="left" fontSize={0.02} color={colors.teal}>Scores</Text>
      <VRBtn name="vr-bottle-players-close" label="Close" x={W / 2 - 0.07} y={H / 2 - 0.035} w={0.11} h={0.045} fontSize={0.015} onPress={onClose} />
      {rows.map((r, i) => {
        const y = H / 2 - 0.095 - i * 0.055;
        return (
          <group key={r.seat} position={[0, y, 0.004]}>
            <Text raycast={noRaycast} position={[-W / 2 + 0.03, 0, 0]} anchorX="left" fontSize={0.019} maxWidth={0.4} color={r.seat === seat ? colors.teal : colors.text}>
              {`${r.name.slice(0, 20)}${r.seat === seat ? ' (you)' : ''}`}
            </Text>
            <Text raycast={noRaycast} position={[0.12, 0, 0]} anchorX="right" fontSize={0.019} color={colors.text}>{`${r.score}`}</Text>
            {owner && r.seat !== seat && (
              <VRBtn name={`vr-bottle-kick-${r.seat}`} label="Remove" x={W / 2 - 0.1} y={0} w={0.15} h={0.045} fontSize={0.014} disabled={busy}
                onPress={() => void tableAction(pid, 'kick', { seat: r.seat })} />
            )}
          </group>
        );
      })}
    </group>
  );
}

function LobbyOptions({ pid, options, busy }: { pid: string; options: BottleOptions; busy: boolean }) {
  const set = (patch: Partial<BottleOptions>) => void tableAction(pid, 'configure', { options: { ...options, ...patch } });
  return <>
    <Text raycast={noRaycast} position={[-W / 2 + 0.03, 0.155, 0.004]} anchorX="left" fontSize={0.015} color={colors.muted}>Mode</Text>
    <VRBtn name="vr-bottle-mode-tod" label={OPTION_LABELS.mode.tod} x={-0.13} y={0.155} w={0.24} h={0.05} fontSize={0.015} active={options.mode === 'tod'} disabled={busy} onPress={() => set({ mode: 'tod' })} />
    <VRBtn name="vr-bottle-mode-spin" label={OPTION_LABELS.mode.spin} x={0.13} y={0.155} w={0.24} h={0.05} fontSize={0.015} active={options.mode === 'spin'} disabled={busy} onPress={() => set({ mode: 'spin' })} />
    <Text raycast={noRaycast} position={[-W / 2 + 0.03, 0.095, 0.004]} anchorX="left" fontSize={0.015} color={colors.muted}>Spice</Text>
    <VRBtn name="vr-bottle-spice-mild" label="Mild" x={-0.13} y={0.095} w={0.24} h={0.05} fontSize={0.015} active={options.spice === 'mild'} disabled={busy} onPress={() => set({ spice: 'mild' })} />
    <VRBtn name="vr-bottle-spice-silly" label="Silly" x={0.13} y={0.095} w={0.24} h={0.05} fontSize={0.015} active={options.spice === 'silly'} disabled={busy} onPress={() => set({ spice: 'silly' })} />
    <Text raycast={noRaycast} position={[-W / 2 + 0.03, 0.035, 0.004]} anchorX="left" fontSize={0.015} color={colors.muted}>Length</Text>
    {ROUND_CHOICES.map((r, i) => (
      <VRBtn key={r} name={`vr-bottle-rounds-${r}`} label={r === 0 ? 'Endless' : String(r)} x={-0.2 + i * 0.105} y={0.035} w={r === 0 ? 0.1 : 0.09} h={0.05} fontSize={0.014}
        active={options.rounds === r} disabled={busy} onPress={() => set({ rounds: r })} />
    ))}
  </>;
}

export function BottleVRPanel({ def, pid, worldId, entry, table, seat, me }: VRPanelProps) {
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const [players, setPlayers] = useState(false);
  const owner = useIsWorldOwner(worldId, me);
  const st = table.state;

  if (seat < 0) return <VRJoinPanel def={def} pid={pid} entry={entry} worldId={worldId} y={1.0} />;

  const g = (st.phase !== 'lobby' ? st.g : null) as BottleState | null;
  const busy = entry.busy;
  const H = 0.5;
  const top = H / 2;
  let body: React.ReactNode = null;
  let corner = '';

  if (st.phase === 'lobby' || !g) {
    const options: BottleOptions = { mode: 'tod', spice: 'mild', rounds: 0, ...(st.options ?? {}) };
    body = <>
      <LobbyOptions pid={pid} options={options} busy={busy} />
      <group position={[0, -0.155, 0]}><VRLobbyBody def={def} pid={pid} entry={entry} /></group>
    </>;
    corner = 'Spin the Bottle';
  } else if (st.phase === 'over') {
    body = <>
      <Text raycast={noRaycast} position={[0, 0.08, 0.004]} fontSize={0.024} maxWidth={W - 0.06} textAlign="center" color={colors.teal}>{winnerLine(st, g, seat)}</Text>
      <VRBtn name="vr-bottle-rematch" label="Rematch" x={-0.19} y={-0.04} w={0.34} h={0.07} active disabled={busy} onPress={() => void tableAction(pid, 'rematch')} />
      <VRBtn name="vr-bottle-leaders-over" label="Leaderboard" x={0.19} y={-0.04} w={0.34} h={0.07} active={board} onPress={() => { setRules(false); setPlayers(false); setBoard((v) => !v); }} />
    </>;
    corner = `You: ${g.players[seat]?.score ?? 0} pts`;
  } else {
    const n = playerCount(st), need = majorityOf(n);
    const amTarget = g.target === seat;
    const passesLeft = Math.max(0, g.passLimit - (g.players[seat]?.passes ?? 0));
    const showCard = (g.phase === 'card' || g.phase === 'cheer') && g.card;
    const ay = -top + 0.13; // big action row
    const actions: React.ReactNode[] = [];
    if (g.phase === 'spin' && g.spinner === seat) {
      actions.push(<VRBtn key="spin" name="vr-bottle-spin" label="SPIN" x={0} y={ay} w={0.5} h={0.085} fontSize={0.03} active disabled={busy}
        onPress={() => void sendMove(pid, { type: 'spin', power: 0.6 + Math.random() * 0.4 })} />);
    }
    if (g.phase === 'choose' && g.chooser === seat) {
      actions.push(<VRBtn key="t" name="vr-bottle-truth" label="Truth (+1)" x={-0.19} y={ay} w={0.34} h={0.085} fontSize={0.024} disabled={busy} onPress={() => void sendMove(pid, { type: 'choose', pick: 'truth' })} />);
      actions.push(<VRBtn key="d" name="vr-bottle-dare" label="Dare (+2)" x={0.19} y={ay} w={0.34} h={0.085} fontSize={0.024} disabled={busy} onPress={() => void sendMove(pid, { type: 'choose', pick: 'dare' })} />);
    }
    if (g.phase === 'card' && amTarget) {
      actions.push(<VRBtn key="done" name="vr-bottle-done" label="Done" x={-0.19} y={ay} w={0.34} h={0.085} fontSize={0.026} active disabled={busy} onPress={() => void sendMove(pid, { type: 'done' })} />);
      actions.push(<VRBtn key="pass" name="vr-bottle-pass" label={`Pass (${passesLeft} left)`} x={0.19} y={ay} w={0.34} h={0.085} fontSize={0.02} disabled={busy || passesLeft <= 0} onPress={() => void sendMove(pid, { type: 'pass' })} />);
    }
    if ((g.phase === 'card' || g.phase === 'cheer') && !amTarget && g.target !== null) {
      const clapped = g.claps.includes(seat);
      actions.push(<VRBtn key="clap" name="vr-bottle-clap" label={clapped ? 'Clapped!' : 'Clap (+1 for them)'} x={0} y={ay} w={0.5} h={0.085} fontSize={0.022} disabled={busy || clapped} onPress={() => void sendMove(pid, { type: 'clap' })} />);
    }
    const by = -top + 0.04; // small bottom row
    const voted = g.endVotes.includes(seat);
    body = <>
      <Text raycast={noRaycast} position={[0, top - 0.095, 0.004]} fontSize={0.021} maxWidth={W - 0.06} textAlign="center"
        color={actions.length ? colors.teal : colors.text}>{statusLine(st, g, seat)}</Text>
      {showCard ? (
        <Text raycast={noRaycast} position={[0, top - 0.125, 0.004]} anchorY="top" fontSize={0.022} lineHeight={1.25} maxWidth={W - 0.08} textAlign="center"
          color={g.card!.type === 'dare' ? '#ffb1a8' : '#b4e2ff'}>{g.card!.text}</Text>
      ) : g.phase === 'spin' && g.spinner === seat ? (
        <Text raycast={noRaycast} position={[0, top - 0.15, 0.004]} fontSize={0.015} color={colors.muted}>Tap SPIN — or pinch the bottle and flick it</Text>
      ) : lastLine(g) ? (
        <Text raycast={noRaycast} position={[0, top - 0.15, 0.004]} fontSize={0.015} color={colors.muted}>{lastLine(g)!}</Text>
      ) : null}
      {actions}
      <VRBtn name="vr-bottle-end" label={voted ? 'Keep playing' : `Vote to end${g.endVotes.length ? ` (${g.endVotes.length}/${need})` : ''}`} x={-W / 2 + 0.13} y={by} w={0.22} h={0.045} fontSize={0.013}
        active={voted} disabled={busy} onPress={() => void sendMove(pid, { type: 'end', on: !voted })} />
      {g.phase === 'card' && (
        <VRBtn name="vr-bottle-skip" label={`Skip card${g.skipVotes.length ? ` (${g.skipVotes.length}/${need})` : ''}`} x={W / 2 - 0.13} y={by} w={0.22} h={0.045} fontSize={0.013}
          disabled={busy || g.skipVotes.includes(seat)} onPress={() => void sendMove(pid, { type: 'skip' })} />
      )}
    </>;
    corner = `${g.rounds ? `Round ${Math.min(g.round, g.rounds)}/${g.rounds}` : 'Endless'} · you ${g.players[seat]?.score ?? 0}`;
  }

  return (
    <SeatPanelMount chair={def.chairs[seat]} height={H}>
      <Surface width={W} height={H} color={colors.panel} />
      {body}
      <Text raycast={noRaycast} position={[-W / 2 + 0.02, top - 0.035, 0.004]} anchorX="left" fontSize={0.014} color={colors.muted}>{corner}</Text>
      <VRBtn label="Leave" x={W / 2 - 0.07} y={top - 0.035} w={0.11} h={0.045} fontSize={0.014} onPress={() => leaveTable(pid)} />
      <VRBtn name="vr-game-howto" label="?" x={W / 2 - 0.155} y={top - 0.035} w={0.05} h={0.045} fontSize={0.016} active={rules}
        onPress={() => { setBoard(false); setPlayers(false); setRules((v) => !v); }} />
      <VRBtn name="vr-game-leaders" label="Leaders" x={W / 2 - 0.25} y={top - 0.035} w={0.12} h={0.045} fontSize={0.014} active={board}
        onPress={() => { setRules(false); setPlayers(false); setBoard((v) => !v); }} />
      {g && st.phase === 'playing' && (
        <VRBtn name="vr-bottle-players" label="Scores" x={W / 2 - 0.37} y={top - 0.035} w={0.11} h={0.045} fontSize={0.014} active={players}
          onPress={() => { setRules(false); setBoard(false); setPlayers((v) => !v); }} />
      )}
      {rules && <group position={[0, 0, 0.02]}><VRRulesPanel def={def} y={-0.31} onClose={() => setRules(false)} /></group>}
      {board && <group position={[0, 0, 0.02]}><VRLeaderboardPanel def={def} worldId={worldId} refreshKey={st.gameId} onClose={() => setBoard(false)} /></group>}
      {players && g && <PlayersPanel pid={pid} st={st} g={g} seat={seat} owner={owner} busy={busy} onClose={() => setPlayers(false)} />}
      {entry.error && <Text raycast={noRaycast} position={[0, -top - 0.025, 0.004]} fontSize={0.014} color="#ff9b9b">{entry.error}</Text>}
    </SeatPanelMount>
  );
}

export default BottleVRPanel;
